import { inject } from '@angular/core';
import { Router, UrlTree, type CanActivateFn } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';

import { AuthService } from './auth.service';
import { User } from '../models/user.model';

/**
 * Restringe el acceso a las rutas de administración a usuarios con rol `ROLE_ADMIN`.
 *
 * El usuario se hidrata de forma fire-and-forget en el `APP_INITIALIZER`, por lo que en
 * una navegación directa (deep-link / recarga) `currentUser()` puede ser `null` aunque
 * haya token válido. En ese caso pedimos el usuario a `/api/auth/me` antes de decidir,
 * para no expulsar a un admin legítimo.
 */
export const adminGuard: CanActivateFn = (_route, state): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const denied = () => router.createUrlTree(['/dashboard']);
  const isAdmin = (user: User | null): boolean | UrlTree =>
    user?.roles.includes('ROLE_ADMIN') ? true : denied();

  const user = authService.currentUser();
  if (user) {
    return isAdmin(user);
  }

  if (!authService.getAccessToken()) {
    return router.createUrlTree(['/auth/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  // Hay token pero el usuario aún no está hidratado: lo pedimos y luego decidimos.
  return authService.fetchCurrentUser().pipe(
    map(fetched => isAdmin(fetched)),
    catchError(() => of(denied()))
  );
};
